<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import axios from "axios";
import { authHeader } from "../../../helpers/auth-header";

import Loading from "@/components/Loading";
import ConfirmationModal from "@/components/modals/confirmation-modal";
import MessageModal from "@/components/modals/message-modal";

import InputGroup from "@/components/forms/input-group";
import SelectGroup from "@/components/forms/select-group";
import SaveButton from "@/components/buttons/save-button";
import DatePicker from 'vue2-datepicker';
import moment from 'moment';

/**
 * Orders Component
 */
export default {
    components: {
        Layout,
        PageHeader,
        ConfirmationModal,
        MessageModal,
        InputGroup,
        SelectGroup,
        SaveButton,
        DatePicker,
        Loading,
    },
    data() {
        const id = this.$route.params.id;
        const modeName = id === 'add' ? 'Add' : 'Edit';

        return {
            id: id,
            title: `Staff Block Date ${modeName}`,
            modeName,
            items: [
                {
                    text: "Staff Block Date"
                },
                {
                    text: modeName,
                    active: true,
                },
            ],

            data: {
                staff_id: null,
                date: null,
                to_date: null,
                start: null,
                end: null,
                is_full_day: 1,
                is_recurring_block: 0,
                recurring_day: null
            },
            staffOptions: [],
            dayOptions: [
                {
                    "value": "sunday",
                    "name": "Sunday"
                },
                {
                    "value": "monday",
                    "name": "Monday"
                },
                {
                    "value": "tuesday",
                    "name": "Tuesday"
                },
                {
                    "value": "wednesday",
                    "name": "Wednesday"
                },
                {
                    "value": "thursday",
                    "name": "Thursday"
                },
                {
                    "value": "friday",
                    "name": "Friday"
                },
                {
                    "value": "saturday",
                    "name": "Saturday"
                },
            ],
            loading: false,
            errors: {},
        };
    },
    computed: {
        
    },
    async mounted() {
        this.retrieveStaffs().then((staffs) => {
            this.staffOptions = staffs.map((staff) => ({
                name: staff.name,
                value: staff.id,
            }));
        });

        if (this.modeName !== 'Add') {
            const result = await axios.get(`${process.env.VUE_APP_APIURL}/staffblock/${this.id}`, {
                headers: authHeader(),
            });
            if (result && result.data['code'] === 'success') {
                const data = result.data['data']['staffBlock'];
                for (const [key, item] of Object.entries(data)) {
                    if (key in this.data && typeof item === 'string') {
                        this.data[key] = item.trim();
                    } else {
                        this.data[key] = item;
                    }
                }

                if (this.data.start || this.data.end) {
                    this.data.is_full_day = 0;
                }
            }
        }
    },
    methods: {
        async retrieveStaffs() {
            this.loading = true;
            let staffs = [];

            try {
                const item = await axios.get(`${process.env.VUE_APP_APIURL}/staff`, {
                    headers: authHeader(),
                });

                const result = await item.data;
                if (result['code'] === 'success') {
                    staffs = result['data'].staffs;
                }
            } catch (e) {
                console.error(e);
            }

            this.loading = false;
            return staffs;
        },

        async save() {
            this.loading = true;
            this.errors = {};

            let result;
            if (this.modeName === 'Add') {
                const requestData = this.data;
                result = await axios.post(`${process.env.VUE_APP_APIURL}/staffblock`, requestData, {
                    headers: authHeader(),
                });
            } else {
                const filteredPairs = Object.entries(this.data).filter(([key]) => !['password', 'password_confirmation'].includes(key));
                const requestData = Object.fromEntries(filteredPairs);
                result = await axios.put(`${process.env.VUE_APP_APIURL}/staffblock/${this.id}`, requestData, {
                    headers: authHeader(),
                });
            }

            if (result) {
                if (result.data['code'] === 'success') {
                    if (this.modeName === 'Add') {
                        this.$router.back();
                    } else {
                        this.$refs.messageModal.showModal('Your record has been updated successfully');
                    }
                    this.loading = false;
                } else if (result.data['code'] === 'invalid_field') {
                    this.errors = result.data['errors'];
                    this.loading = false;
                }
            }
            this.loading = false;
        },

        disableDates(date) {
            const val = moment(date);
            const today = moment();

            if (val.isBefore(today, 'day')) {
                return true;
            }

            return false;
        },
    }
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <ConfirmationModal ref="confirmationModal" />
        <MessageModal ref="messageModal" />
        <Loading v-if="loading" />
        <div class="card p-3">
            <div class="row no-gutters">
                <div class="col-12 col-md-6 col-lg-4 pl-2">
                    <SelectGroup id="staff_id" v-model="data.staff_id" displayName="Staff*" :options="staffOptions" :error="errors.staff_id" />
                </div>
            </div>

            <div class="row no-gutters mb-4">
                <div class="col-12 col-md-6 col-lg-4 pl-2">
                    <label class="d-block w-100">Set recurring block?*</label>
                    <div class="d-flex radio">
                        <input type="radio" id="recurring_yes" :value="1" name="is_recurring_block" role="button" v-model="data.is_recurring_block">
                        <label class="mb-0 ml-2 mr-3" for="recurring_yes" role="button">Yes</label>
                        <input type="radio" class="ml-5" id="recurring_no" :value="0" name="is_recurring_block" role="button" v-model="data.is_recurring_block">
                        <label class="mb-0 ml-2 mr-3" for="recurring_no" role="button">No</label>
                    </div>
                </div>
            </div>

            <div class="row no-gutters" v-if="data.is_recurring_block == 1">
                <div class="col-12 col-md-6 col-lg-4 pl-2">
                    <SelectGroup id="recurring_day" v-model="data.recurring_day" displayName="Day*" :options="dayOptions" :error="errors.recurring_day" />
                </div>
            </div>

            <div class="row no-gutters mb-4">
                <div class="col-12 col-md-6 col-lg-4 pl-2">
                    <label class="d-block w-100">Is full day block?*</label>
                    <div class="d-flex radio">
                        <input type="radio" id="yes" :value="1" name="is_full_day" role="button" v-model="data.is_full_day">
                        <label class="mb-0 ml-2 mr-3" for="yes" role="button">Yes</label>
                        <input type="radio" class="ml-5" id="no" :value="0" name="is_full_day" role="button" v-model="data.is_full_day">
                        <label class="mb-0 ml-2 mr-3" for="no" role="button">No</label>
                    </div>
                </div>
            </div>

            <div class="row no-gutters">
                <div class="col-12 col-md-6 col-lg-4 pl-2">
                    <!-- <InputGroup type="date" id="date" displayName="Date" v-model="data.date" :error="errors.date" /> -->
                    <label for="date">From Date*</label>
                    <date-picker
                        v-model="data.date"
                        :default-value="new Date()"
                        :disabled-date="disableDates"
                        valueType="format"
                        :editable="false"
                    />
                    <div class="alert alert-danger mt-3" role="alert" v-if="errors.date && errors.date.length > 0">
                        {{ errors.date[0] }}
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4 pl-2">
                    <label for="to_date">To Date*</label>
                    <date-picker
                        v-model="data.to_date"
                        :default-value="new Date()"
                        :disabled-date="disableDates"
                        valueType="format"
                        :editable="false"
                    />
                    <div class="alert alert-danger mt-3" role="alert" v-if="errors.to_date && errors.to_date.length > 0">
                        {{ errors.to_date[0] }}
                    </div>
                </div>
            </div>
            <div class="row no-gutters mt-3" v-if="data.is_full_day == 0">
                <div class="col-12 col-md-6 col-lg-4 pl-2">
                    <InputGroup type="time" id="start" displayName="Start Time" v-model="data.start" :error="errors.start" class="mb-1" />
                    <span class="help-block">Leave blank for full day leave.</span>
                </div>
                <div class="col-12 col-md-6 col-lg-4 pl-2">
                    <InputGroup type="time" id="end" displayName="End Time" v-model="data.end" :error="errors.end" class="mb-1" />
                    <span class="help-block">Leave blank for full day leave.</span>
                </div>
            </div>
            <div class="row mt-5 pl-2">
                <div class="col-12">
                    <SaveButton @click="save" />
                </div>
            </div>
        </div>
    </Layout>
</template>